// Centralized marketing/analytics tag manager.
// Single GTM container. Regional Meta Pixel routing: EAST-region leads -> East pixel,
// WEST-region leads -> West pixel. Canonical SUCCESSFUL_LEAD fires only after persistence.
import { getAttribution } from "./utm";

export const TRACKING = {
  gtmId: process.env.REACT_APP_GTM_ID || "",
  ga4Id: process.env.REACT_APP_GA4_MEASUREMENT_ID || "",
  adsConversionId: process.env.REACT_APP_GOOGLE_ADS_CONVERSION_ID || "",
  adsConversionLabel: process.env.REACT_APP_GOOGLE_ADS_CONVERSION_LABEL || "",
  metaPixelEast: process.env.REACT_APP_META_PIXEL_EAST || process.env.REACT_APP_META_PIXEL_ID || "",
  metaPixelWest: process.env.REACT_APP_META_PIXEL_WEST || "",
};

let initialized = false;
const metaInited = new Set();

export function pushDataLayer(obj) {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(obj);
}

function loadGTM(id) {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ "gtm.start": new Date().getTime(), event: "gtm.js" });
  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtm.js?id=${id}`;
  document.head.appendChild(s);
  const noscript = document.createElement("noscript");
  const iframe = document.createElement("iframe");
  iframe.src = `https://www.googletagmanager.com/ns.html?id=${id}`;
  iframe.height = "0"; iframe.width = "0"; iframe.style.display = "none"; iframe.style.visibility = "hidden";
  noscript.appendChild(iframe);
  document.body.insertBefore(noscript, document.body.firstChild);
}

function loadGtag(primaryId, extraIds) {
  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${primaryId}`;
  document.head.appendChild(s);
  window.dataLayer = window.dataLayer || [];
  window.gtag = function () { window.dataLayer.push(arguments); };
  window.gtag("js", new Date());
  window.gtag("config", primaryId);
  extraIds.filter(Boolean).forEach((id) => window.gtag("config", id));
}

function loadMetaBase() {
  if (window.fbq) return;
  /* eslint-disable */
  !(function (f, b, e, v, n, t, s) {
    if (f.fbq) return; n = f.fbq = function () { n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments); };
    if (!f._fbq) f._fbq = n; n.push = n; n.loaded = !0; n.version = "2.0"; n.queue = [];
    t = b.createElement(e); t.async = !0; t.src = v; s = b.getElementsByTagName(e)[0]; s.parentNode.insertBefore(t, s);
  })(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");
  /* eslint-enable */
}

function ensureMetaPixel(id) {
  if (!id) return false;
  loadMetaBase();
  if (!metaInited.has(id)) {
    window.fbq("init", id);
    metaInited.add(id);
  }
  return true;
}

export function initTracking() {
  if (initialized) return;
  initialized = true;
  window.dataLayer = window.dataLayer || [];
  try {
    if (TRACKING.gtmId) loadGTM(TRACKING.gtmId);
    if (TRACKING.ga4Id || TRACKING.adsConversionId) {
      loadGtag(TRACKING.ga4Id || TRACKING.adsConversionId, [TRACKING.ga4Id, TRACKING.adsConversionId]);
    }
    // Default pixel = East. PageView fires once on the default pixel only.
    if (ensureMetaPixel(TRACKING.metaPixelEast)) {
      window.fbq("trackSingle", TRACKING.metaPixelEast, "PageView");
    }
  } catch (e) { /* no-op */ }
}

export function trackPageView(path) {
  pushDataLayer({ event: "page_view", page_path: path });
  if (typeof window.gtag === "function" && TRACKING.ga4Id) window.gtag("event", "page_view", { page_path: path });
}

export function trackViewContent(name, meta = {}) {
  pushDataLayer({ event: "view_content", content_name: name, ...meta });
  if (typeof window.fbq === "function" && TRACKING.metaPixelEast) {
    window.fbq("trackSingle", TRACKING.metaPixelEast, "ViewContent", { content_name: name, ...meta });
  }
}

// Generic GA4 + dataLayer event (NON-PII params only — never pass name/phone/email).
export function trackEvent(name, params = {}) {
  try {
    pushDataLayer({ event: name, ...params });
    if (typeof window.gtag === "function" && TRACKING.ga4Id) window.gtag("event", name, params);
  } catch (e) { /* no-op */ }
}

function pixelForRegion(region) {
  const r = (region || "").toUpperCase();
  if (r === "EAST") return TRACKING.metaPixelEast;
  if (r === "WEST") return TRACKING.metaPixelWest;
  return ""; // North/South/Central/general -> no pixel configured
}

// CANONICAL SUCCESSFUL LEAD — call ONLY after backend confirms persistence
export function trackSuccessfulLead({ leadId, source, region, projectName, location } = {}) {
  const attribution = getAttribution();
  const base = {
    lead_id: leadId || null, lead_source: source || "website", region: region || null,
    content_name: projectName || null, location: location || null, ...attribution,
  };
  pushDataLayer({ event: "successful_lead", ...base });
  if (typeof window.gtag === "function" && TRACKING.ga4Id) {
    window.gtag("event", "generate_lead", { ...base, currency: "INR", value: 0, transaction_id: leadId || undefined });
  }
  if (typeof window.gtag === "function" && TRACKING.adsConversionId && TRACKING.adsConversionLabel) {
    window.gtag("event", "conversion", {
      send_to: `${TRACKING.adsConversionId}/${TRACKING.adsConversionLabel}`,
      currency: "INR", value: 0, transaction_id: leadId || undefined,
    });
  }
  const pixel = pixelForRegion(region);
  if (pixel && ensureMetaPixel(pixel)) {
    window.fbq("trackSingle", pixel, "Lead", { content_name: projectName || undefined });
  }
}
